/**
 * OwnerFlagsInbox — data-gap reports flagged against this uni or
 * its degrees. Owners can:
 *   * Click "Open editor" → deep-links straight to the relevant
 *     course / uni edit page.
 *   * Click "Dismiss" → marks the flag `dismissed` (owner can't
 *     "action" it here — that comes from actually verifying the
 *     field in the RegistryFieldEditor).
 *
 * Fetched from GET /api/uni-portal/university/{id}/flags — owner-
 * gated, returns rows with `status="new"` by default.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, PencilLine, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function OwnerFlagsInbox({ universityId }) {
  const [items, setItems] = useState(null);
  const [dismissing, setDismissing] = useState({});

  useEffect(() => {
    if (!universityId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/uni-portal/university/${universityId}/flags?status=new&limit=25`,
        );
        if (!cancelled) setItems(data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [universityId]);

  const dismiss = async (id) => {
    setDismissing((s) => ({ ...s, [id]: true }));
    try {
      await api.patch(`/uni-portal/flags/${id}`, { status: "dismissed" });
      setItems((rows) => (rows || []).filter((r) => r.id !== id));
      toast.success("Flag dismissed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't dismiss");
    } finally {
      setDismissing((s) => ({ ...s, [id]: false }));
    }
  };

  if (items === null) return null;         // loading — silent
  if (items.length === 0) return null;     // nothing to nag — silent

  return (
    <div
      className="mt-8 border border-amber-300 rounded-xl bg-amber-50/70 overflow-hidden"
      data-testid="uni-portal-flags-inbox"
    >
      <div className="px-5 py-3 bg-amber-100/60 border-b border-amber-200">
        <div className="flex items-center gap-2 text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          <div>
            <div className="font-editorial text-lg leading-tight">
              Flags on your courses ({items.length})
            </div>
            <div className="text-[11px] text-amber-900/70 leading-snug">
              Families flagged these fields as wrong or missing.
              Open the editor to fix, or dismiss if you disagree —
              WUA editorial reviews dismissals weekly.
            </div>
          </div>
        </div>
      </div>
      <ul className="divide-y divide-amber-200">
        {items.map((f) => {
          const editorHref = f.degree_id
            ? `/uni-portal/degree/${f.degree_id}`
            : `/uni-portal/uni/${universityId}`;
          const target = f.degree_title || "University";
          const busy = !!dismissing[f.id];
          return (
            <li
              key={f.id}
              className="px-5 py-3 flex items-start gap-3 flex-wrap"
              data-testid={`uni-portal-flag-${f.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-stone-900">
                  <span className="text-[10px] font-underground-sc tracking-widest text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 mr-2">
                    {f.section || "—"}
                  </span>
                  <span className="font-medium">{f.field || "empty cell"}</span>
                  <span className="text-stone-400"> · </span>
                  <span className="text-stone-600">{target}</span>
                </div>
                {f.hint && (
                  <div className="mt-1 text-[12px] text-stone-600 italic leading-snug">
                    "{f.hint.length > 220 ? f.hint.slice(0, 220) + "…" : f.hint}"
                  </div>
                )}
                <div className="mt-1 text-[10px] font-mono text-stone-400">
                  {f.reporter_relation || "someone"}
                  {f.created_at ? ` · ${new Date(f.created_at).toLocaleDateString()}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link to={editorHref}>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`uni-portal-flag-open-${f.id}`}
                  >
                    <PencilLine className="h-3 w-3 mr-1" />
                    Open editor
                  </Button>
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => dismiss(f.id)}
                  data-testid={`uni-portal-flag-dismiss-${f.id}`}
                  className="text-stone-500 hover:text-stone-900"
                >
                  <X className="h-3 w-3 mr-1" />
                  {busy ? "…" : "Dismiss"}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
